import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ConvertToString from "../../../components/Converter";
import ToolTip, { IconType } from "../../../components/ToolTip";
import imageDE from "../../../images/20231009_Druckpfahl Kunststoff_DE.png";
import imageEN from "../../../images/20231009_Druckpfahl Kunststoff_EN.png";
import imageES from "../../../images/20231009_Druckpfahl Kunststoff_ES.png";
import { berechneAchsabstand, berechneC } from "../../../services/berechnePileHead";
import { useGetTraggliedDatenQuery } from "../../../services/API/titanApi";
import { selectBerechneInnere } from "../../../store/slices/innereSlice";
import { selectTragglied } from "../../../store/slices/traggliedSlice";
import BestimmungDesUebergangsrohres from "./BestimmungDesUebergangsrohres/BestimmungDesUebergangsrohres";
import LageKopfplatteUndUbergangsrohr from "./LageKopfplatteUndUbergangsrohr/LageKopfplatteUndUbergangsrohr";

export default function DruckHDPE() {
  const { t, i18n } = useTranslation("translation");
  const e_min_formula = `\\displaystyle E = 3 \\cdot D + U`;
  const b_formula = `\\displaystyle B = D`;
  const c_formula = `\\displaystyle C_{min} = D + U`;

  // #region Redux Store Variables
  const tragglied = useSelector(selectTragglied);
  const { data: traggliedDaten } = useGetTraggliedDatenQuery(tragglied.title);
  const innereData = useSelector(selectBerechneInnere(traggliedDaten));
  // #endregion

  const [achsabstand, setAchsabstand] = useState(0);
  const [c, setC] = useState(0);

  useEffect(() => {
    let achsabstand = berechneAchsabstand(
      innereData?.durchmesserDesRohres,
      traggliedDaten?.kopfkonstruktion?.kantenlaenge
    );
    setAchsabstand(achsabstand);

    let c = berechneC(
      innereData?.durchmesserDesRohres,
      traggliedDaten?.kopfkonstruktion?.kantenlaenge
    );
    setC(c);
    // Depend on the primitive value, not on `innereData`: selectBerechneInnere
    // is created inline on every render, so `innereData` is a new object
    // reference each time. Listing it here re-ran the effect on every render,
    // and its setState re-rendered — an infinite loop ("Maximum update depth
    // exceeded") that froze the page once this component mounted.
  }, [traggliedDaten, innereData?.durchmesserDesRohres]);
  return (
    <>
      <BestimmungDesUebergangsrohres
        traggliedDaten={traggliedDaten}
        durchmesserDesRohres={innereData?.durchmesserDesRohres}
        wanddickeUebergangsrohr={Number(traggliedDaten?.uebergangsrohr?.hdpe_rohr?.wanddicke)}
        einbindelaengeBaugrund={Number(traggliedDaten?.uebergangsrohr?.min_lb_druck)}
        laenge={Number(traggliedDaten?.uebergangsrohr?.laenge_druck)}
      />

      <LageKopfplatteUndUbergangsrohr
        kopfkonstruktion={traggliedDaten?.kopfkonstruktion}
        minimalerRandabstand={innereData?.minimalerRandabstand}
        durchmesserDesRohres={innereData?.durchmesserDesRohres}
      />

      <br />
      <div className="row">
        <div className="col-md-4">
          <div className="row">
            <div className="col-md-12">
              <h3 style={{ marginBottom: "0" }}>{t("aeussre.additionalReinforcement")}</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <h6>{t("aeussre.additionalUpperReinforcement")}</h6>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>&Phi;</p>
                </div>
                <div className="col-4">
                  <span className="input">
                    {ConvertToString(traggliedDaten?.obere_zusatzbewehrung?.durchmesser)}
                  </span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[mm]</span>
                </div>
                <div className="col-1">
                  <span></span>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>n</p>
                </div>
                <div className="col-4">
                  <span className="input">
                    {ConvertToString(traggliedDaten?.obere_zusatzbewehrung?.n)}
                  </span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[{t("inner.unitlagen")}]</span>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>
                    E<sub>min</sub>
                  </p>
                </div>
                <div className="col-4">
                  <span className="input">{ConvertToString(achsabstand, 0)}</span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[mm]</span>
                </div>
                <div className="col-1">
                  <ToolTip id={"e_min_formula"} type={IconType.FORMULA} formula={e_min_formula} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>B</p>
                </div>
                <div className="col-4">
                  <span className="input">
                    {ConvertToString(innereData?.durchmesserDesRohres, 0)}
                  </span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[mm]</span>
                </div>
                <div className="col-1">
                  <ToolTip id={"b_formula"} type={IconType.FORMULA} formula={b_formula} />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <h6>{t("aeussre.additionalLowerReinforcement")}</h6>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>&Phi;</p>
                </div>
                <div className="col-4">
                  <span className="input">
                    {ConvertToString(traggliedDaten?.untere_zusatzbewehrung?.durchmesser)}
                  </span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[mm]</span>
                </div>
                <div className="col-1">
                  <span></span>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>n</p>
                </div>
                <div className="col-4">
                  <span className="input">
                    {ConvertToString(traggliedDaten?.untere_zusatzbewehrung?.n)}
                  </span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[{t("inner.unitlagen")}]</span>
                </div>
                <div className="col-1">
                  <span></span>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <p></p>
                </div>
                <div className="col-1">
                  <p>
                    C<sub>min</sub>
                  </p>
                </div>
                <div className="col-4">
                  <span className="input">{ConvertToString(c, 0)}</span>
                </div>
                <div className="col-1">
                  <span>&nbsp;[mm]</span>
                </div>
                <div className="col-1">
                  <ToolTip id={"c_formula"} type={IconType.FORMULA} formula={c_formula} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <img
            src={i18n.language == "de-DE" ? imageDE : i18n.language == "es-ES" ? imageES : imageEN}
            style={{ width: "100%" }}
            alt=""
          />
        </div>
      </div>
    </>
  );
}
